import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { CheckCircle, Clock, AlertCircle, Loader } from "lucide-react";
import { SWAP_PROGRESS_STATUS } from "../../../constants/swapData";

const STATUS_CONFIG = {
  [SWAP_PROGRESS_STATUS.SWAP_COMPLETED]: {
    icon: CheckCircle,
    iconColor: "#22c55e",
    textColor: "success.main",
    message: "Swap Completed Successfully",
  },
  [SWAP_PROGRESS_STATUS.SWAP_INCOMPLETE]: {
    icon: AlertCircle,
    iconColor: "#ef4444",
    textColor: "error.main",
    message: "Swap Incomplete, Please Try Again",
  },
  [SWAP_PROGRESS_STATUS.SWAP_PENDING]: {
    icon: Clock,
    iconColor: "#f59e0b",
    textColor: "warning.main",
    message: "Swap Pending, Awaiting Confirmation",
  },
  [SWAP_PROGRESS_STATUS.SWAP_FAILED]: {
    icon: AlertCircle,
    iconColor: "#ef4444",
    textColor: "error.main",
    message: "Swap Failed, Please Try Again",
  },
  [SWAP_PROGRESS_STATUS.SWAP_ACTIVE]: {
    icon: Loader,
    iconColor: "#3b82f6",
    textColor: "info.main",
    message: "Swap In Progress...",
    animated: true,
  },
  [SWAP_PROGRESS_STATUS.SWAP_IN_PROGRESS]: {
    icon: Loader,
    iconColor: "#3b82f6",
    textColor: "info.main",
    message: "Swap In Progress...",
    animated: true,
  },
  [SWAP_PROGRESS_STATUS.ERROR]: {
    icon: AlertCircle,
    iconColor: "#ef4444",
    textColor: "error.main",
    message: "Error Occurred, Please Try Again",
  },
};

const DEFAULT_CONFIG = {
  icon: Clock,
  iconColor: "#9ca3af",
  textColor: "text.secondary",
  message: "Unknown Status, Please Contact Support",
};

export default function SwapStatusCard({
  swapStatus,
  swapTransactionData,
  isSwapTimeout,
}) {
  const getEffectiveConfig = () => {
    if (isSwapTimeout && swapStatus !== SWAP_PROGRESS_STATUS.SWAP_INCOMPLETE) {
      return {
        icon: AlertCircle,
        iconColor: "#ef4444",
        textColor: "error.main",
        message: "Swap Timed Out, Please Try Again",
      };
    }

    return STATUS_CONFIG[swapStatus] || DEFAULT_CONFIG;
  };

  const config = getEffectiveConfig();
  const IconComponent = config.icon;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: 2,
        border: "2px dashed",
        borderColor: "grey.300",
        borderRadius: 2,
        bgcolor: "white",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              fontWeight={600}
              color="text.primary"
              fontSize="1rem"
            >
              Swap Status
            </Typography>
            {swapTransactionData?.swapStatus && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "text.secondary",
                }}
              >
                {`(${swapTransactionData.swapStatus})`}
              </Typography>
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: config.textColor,
              mt: 0.5,
              fontWeight: 600,
            }}
          >
            {config.message}
          </Typography>
        </Box>

        <Box>
          <IconComponent
            size={30}
            color={config.iconColor}
            className={config.animated ? "animate-spin" : ""}
          />
        </Box>
      </Box>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Paper>
  );
}
